import { getKeycloak, getToken } from './keycloak';
import type {
  Me,
  Cluster,
  CreateClusterPayload,
  UpdateClusterPayload,
  FlowSnapshot,
  QueueStats,
  ScalePayload,
  AuditEntry,
  AuditQuery,
  Snapshot,
  RangePreset,
  DashboardSummary,
  User,
  ListUsersQuery,
  Role,
  PackagesStateResponse,
  PackagesCapability,
  PackagesDaemonStatus,
  DaemonNode,
  StatusResponse,
  NetConflicts,
  ServiceDiagnostics,
  TaskJobs,
  SearchResult,
  RemediationsResponse,
  PerformanceReport,
  CoverageReport,
} from './types';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly path: string;
  constructor(status: number, body: unknown, path: string) {
    super(`api ${status} on ${path}`);
    this.status = status;
    this.body = body;
    this.path = path;
  }
}

export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function doFetch<T>(
  path: string,
  init: RequestInit,
  refreshed: boolean
): Promise<T> {
  const token = await getToken();
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && !refreshed) {
    try {
      await getKeycloak().updateToken(30);
    } catch {
      getKeycloak().login({ redirectUri: window.location.origin });
      throw new ApiError(401, null, path);
    }
    return doFetch<T>(path, init, true);
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) throw new ApiError(res.status, parsed, path);
  return parsed as T;
}

export function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return doFetch<T>(path, init, false);
}

export function getMe(): Promise<Me> {
  return apiFetch<Me>('/api/me');
}

export function getStatus(): Promise<StatusResponse> {
  return apiFetch<StatusResponse>('/api/status');
}

export function getClusterNetConflicts(slug: string): Promise<NetConflicts> {
  return apiFetch<NetConflicts>(`/api/clusters/${slug}/net-conflicts`);
}

export function getRemediations(): Promise<RemediationsResponse> {
  return apiFetch<RemediationsResponse>('/api/remediations');
}

export function resetRemediation(
  cluster: string,
  service: string
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/remediations/${encodeURIComponent(cluster)}/${encodeURIComponent(service)}/reset`,
    { method: 'POST' }
  );
}

export function getServiceDiagnostics(
  slug: string,
  serviceId: string
): Promise<ServiceDiagnostics> {
  return apiFetch<ServiceDiagnostics>(
    `/api/clusters/${slug}/services/${encodeURIComponent(serviceId)}/diagnostics`
  );
}

export function getVncCredentials(): Promise<{ password: string }> {
  return apiFetch<{ password: string }>('/api/mosaic/vnc-credentials');
}

export function getTaskJobs(taskIds: string[]): Promise<TaskJobs> {
  const qs = taskIds.map(id => `task=${encodeURIComponent(id)}`).join('&');
  return apiFetch<TaskJobs>(`/api/sqs/jobs?${qs}`);
}

export function listClusters(): Promise<Cluster[]> {
  return apiFetch<Cluster[]>('/api/clusters');
}

export function createCluster(payload: CreateClusterPayload): Promise<Cluster> {
  return apiFetch<Cluster>('/api/clusters', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateCluster(
  id: string,
  patch: UpdateClusterPayload
): Promise<Cluster> {
  return apiFetch<Cluster>(`/api/clusters/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteCluster(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/clusters/${id}`, { method: 'DELETE' });
}

export interface ClusterHealthcheck {
  ok: boolean;
  cluster?: string;
  health?: unknown;
  error?: string;
}

export function clusterHealthcheck(id: string): Promise<ClusterHealthcheck> {
  return apiFetch<ClusterHealthcheck>(`/api/clusters/${id}/healthcheck`);
}

export function getFlow(slug: string): Promise<FlowSnapshot> {
  return apiFetch<FlowSnapshot>(`/api/flow${buildQuery({ cluster: slug })}`);
}

export function getQueueStats(slug: string): Promise<QueueStats> {
  return apiFetch<QueueStats>(
    `/api/queue/stats${buildQuery({ cluster: slug })}`
  );
}

export function postScale(payload: ScalePayload): Promise<unknown> {
  return apiFetch('/api/scale', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface ActivityEvent {
  action: 'mosaic-view' | 'open-ip';
  cluster?: string;
  target?: string;
  payload?: Record<string, unknown>;
}

export function recordActivity(ev: ActivityEvent): void {
  void apiFetch('/api/activity', {
    method: 'POST',
    body: JSON.stringify(ev),
  }).catch(() => {});
}

export function getAudit(params?: AuditQuery): Promise<AuditEntry[]> {
  return apiFetch<AuditEntry[]>(
    `/api/audit${buildQuery(params as Record<string, unknown>)}`
  );
}

export function getAuditPerformance(params: {
  days: number;
  cluster?: string;
}): Promise<PerformanceReport> {
  return apiFetch<PerformanceReport>(
    `/api/audit/performance${buildQuery(params)}`
  );
}

export function getAuditCoverage(params: {
  days: number;
}): Promise<CoverageReport> {
  return apiFetch<CoverageReport>(`/api/audit/coverage${buildQuery(params)}`);
}

export function getSnapshots(
  slug: string,
  range: RangePreset
): Promise<Snapshot[]> {
  return apiFetch<Snapshot[]>(
    `/api/snapshots${buildQuery({ cluster: slug, range })}`
  );
}

export function searchAll(q: string, limit?: number): Promise<SearchResult> {
  return apiFetch<SearchResult>(`/api/search${buildQuery({ q, limit })}`);
}

export function getDashboardSummary(
  cluster: 'all' | string,
  stack?: string
): Promise<DashboardSummary> {
  return apiFetch<DashboardSummary>(
    `/api/dashboard/summary${buildQuery({ cluster, stack: stack || undefined })}`
  );
}

export function listUsers(query?: ListUsersQuery): Promise<User[]> {
  return apiFetch<User[]>(
    `/api/users${buildQuery(query as Record<string, unknown>)}`
  );
}

export function updateUserRole(
  id: string,
  role: Role,
  reason?: string
): Promise<User> {
  return apiFetch<User>(`/api/users/${id}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role, reason }),
  });
}

export function updateUserActive(id: string, active: boolean): Promise<User> {
  return apiFetch<User>(`/api/users/${id}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });
}

export function getPackagesState(slug: string): Promise<PackagesStateResponse> {
  return apiFetch<PackagesStateResponse>(
    `/api/packages/state${buildQuery({ cluster: slug })}`,
  );
}

export function getPackagesCapability(
  slug: string,
): Promise<PackagesCapability> {
  return apiFetch<PackagesCapability>(
    `/api/packages/capability${buildQuery({ cluster: slug })}`,
  );
}

export type { PackagesDaemonStatus, DaemonNode };

export function getPackagesDaemonStatus(
  slug: string,
): Promise<PackagesDaemonStatus> {
  return apiFetch<PackagesDaemonStatus>(
    `/api/packages/status${buildQuery({ cluster: slug })}`,
  );
}

export function getPkgCheck(slug: string, pkg: string): Promise<unknown> {
  return apiFetch(`/api/packages/check${buildQuery({ cluster: slug, pkg })}`);
}

export function getPkgNodeCheck(
  slug: string,
  host: string,
): Promise<unknown> {
  return apiFetch(
    `/api/packages/node-check${buildQuery({ cluster: slug, host })}`,
  );
}

export function getPkgDeps(
  slug: string,
  host: string,
  pkg: string,
): Promise<unknown> {
  return apiFetch(
    `/api/packages/deps${buildQuery({ cluster: slug, host, pkg })}`,
  );
}

export function getPkgVersions(
  slug: string,
  pkg: string,
): Promise<{ package?: string; versions?: string[] }> {
  return apiFetch(
    `/api/packages/versions${buildQuery({ cluster: slug, pkg })}`,
  );
}

export function getPkgPending(
  slug: string,
): Promise<{ entries?: { package: string; host?: string }[] }> {
  return apiFetch(`/api/packages/pending${buildQuery({ cluster: slug })}`);
}

function pkgPost(path: string, slug: string, body?: unknown): Promise<unknown> {
  return apiFetch(`/api/packages/${path}${buildQuery({ cluster: slug })}`, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export const pkgPause = (slug: string, reason = 'manager') =>
  pkgPost('pause', slug, { reason });
export const pkgResume = (slug: string) => pkgPost('resume', slug);
export const pkgReload = (slug: string) => pkgPost('reload', slug);
export const pkgReconcile = (slug: string) => pkgPost('reconcile', slug);
export const pkgRedeployAll = (slug: string) =>
  pkgPost('redeploy', slug, { all: true });
export const pkgRedeploy = (slug: string, pkgs: string[]) =>
  pkgPost('redeploy', slug, { packages: pkgs });
export const pkgUnforceRedeploy = (
  slug: string,
  target?: { package?: string; host?: string },
) => pkgPost('unforce-redeploy', slug, target ?? {});
export const pkgPin = (slug: string, pkg: string, version: string) =>
  pkgPost('pin', slug, { package: pkg, version });
export const pkgUnpin = (slug: string, pkg: string) =>
  pkgPost('unpin', slug, { package: pkg });
export const pkgPkgPause = (slug: string, pkg: string) =>
  pkgPost('pkg-pause', slug, { package: pkg });
export const pkgPkgResume = (slug: string, pkg: string) =>
  pkgPost('pkg-resume', slug, { package: pkg });
export const pkgUnquarantine = (slug: string, pkg: string) =>
  pkgPost('unquarantine', slug, { package: pkg });
export const pkgNodeRedeploy = (slug: string, host: string) =>
  apiFetch(
    `/api/packages/node/${encodeURIComponent(host)}/redeploy${buildQuery({ cluster: slug })}`,
    { method: 'POST' },
  );
export const pkgNodeHeal = (slug: string, host: string, apply: boolean) =>
  apiFetch(
    `/api/packages/node/${encodeURIComponent(host)}/heal${buildQuery({ cluster: slug, apply })}`,
    { method: 'POST' },
  );
export const pkgTestNodeStart = (
  slug: string,
  host: string,
  pkg: string,
  version: string,
) => pkgPost('test-node/start', slug, { host, pkg, version });
export const pkgTestNodePromote = (slug: string, host: string) =>
  pkgPost('test-node/promote', slug, { host });
export const pkgTestNodeRollback = (slug: string, host: string) =>
  pkgPost('test-node/rollback', slug, { host });
